/** Stateless across bundles. One failed attempt owns its warning and refusal event. */
export interface TimePushRefusalInput {
    campaignId: string;
    encounterId: string;
    hungerOnly?: boolean;
    onRefused?: (reason: string) => void;
}

export function reportTimePushRefusal(input: TimePushRefusalInput, cause: string, detail: Record<string, unknown> = {}, unknown = false): null {
    const codes: Record<string, string> = { 'Target row outside this governing owner': 'target-owner-conflict',
        'GM access required': 'gm-permission', 'Unknown campaign': 'campaign-missing', 'Unknown encounter': 'encounter-missing' };
    const code = codes[cause] ?? cause.replace(/^time-push:/, '').toLowerCase();
    const reason = 'time-push:' + (detail.deferred === true ? 'member-time-deferred'
        : /^[a-z0-9_:.,*/-]+$/.test(code) ? code : unknown ? 'save-unconfirmed' : 'save-refused');
    try { input.onRefused?.(reason); } catch { /* An observer cannot change the disposition. */ }
    // A heartbeat with nothing due is ordinary inactivity, never a failed GM action.
    if (input.hungerOnly && ['nothing-due', 'hunger-off', 'in-combat'].includes(cause)) return null;
    const id = crypto.randomUUID();
    window.dispatchEvent(new CustomEvent('ft:unported', { detail: { mode: 'refused', reason, detail: {
        timePushRefusalId: id, encounterId: input.encounterId, campaignId: input.campaignId, ...detail,
    } } }));
    if (!input.hungerOnly) {
        const compileRefused = cause.startsWith('entity-compile-refused:');
        const deferred = detail.deferred === true;
        const pending = cause === 'member-time-pending';
        const step = deferred ? 'The clock advanced. Their timed work is queued until they can compile.'
            : pending ? 'Their owed time remains queued. Try again when this payment finishes.'
            : compileRefused ? 'Open the creature card to inspect the error before trying the time controls again.'
            : cause === 'entry-threw' ? 'Check the campaign clock and log before using the time controls again.'
            : unknown ? 'Click a time push again to check this saved operation; it will not advance twice.'
            : /campaign-arr|entity-base|resolver-not-loaded|calendar|clock-snapshot/.test(reason) ? 'Wait for the campaign to finish loading, then click the time push again.'
            : /trigger-consent/.test(reason) ? 'Click the time push again and answer its trigger prompt.'
            : 'Click the time push again to try from the current state.';
        const causeText = deferred ? `${String(detail.memberName)} was left out: ${String(detail.compileReason)}.`
            : pending ? `${String(detail.memberName)} has pending timed work (${String(detail.debtReason)}).`
            : compileRefused ? `${String(detail.memberName)} could not compile: ${String(detail.compileReason)}.`
            : /entity-base-timeout/.test(reason) ? 'The encounter or creature loading did not finish in time.'
            : /resync-timeout/.test(reason) ? 'The encounter refresh did not finish in time.'
            : /entity-base/.test(reason) ? 'An encounter or creature could not become ready.'
            : /campaign-arr/.test(reason) ? 'The campaign clock is not loaded.'
            : /calendar|clock-snapshot/.test(reason) ? 'The campaign calendar or clock is not ready.'
            : /stale/.test(reason) ? 'The clock or its participants changed during the save.'
            : unknown ? 'The saved time push could not be confirmed.' : 'The browser could not advance the clock.';
        const diagnostic = /save-unconfirmed|save-refused|target-owner-conflict|gm-permission/.test(reason) ? ` ${cause}` : '';
        const message = `${causeText}${diagnostic} ${step} (${reason})`.replace(/[&<>"']/g,
            ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!);
        const warn = (window as unknown as { displayWarning?: (message: string, diagnostic?: unknown) => void }).displayWarning;
        try { warn?.(message, { timePushRefusalId: id }); } catch { /* A warning cannot alter the save outcome. */ }
    }
    return null;
}
